/**
 * TopBar — persistent chrome bar for non-game routes.
 *
 * Slot layout:
 *   [left slot]  [center: title or wordmark]  [right: cog]
 *
 * Props:
 *   lobby    — when true, replaces the center title with the wordmark
 *              (AppleChar 22px + "사과상자 · Sagwa Box").
 *   hasBack  — when true, replaces the left avatar slot with a back glyph.
 *   title    — center text when lobby=false (ignored when lobby=true).
 *   onAvatar — callback for avatar tap (only fires when hasBack=false).
 *   onBack   — callback for back glyph tap (only fires when hasBack=true).
 *   onCog    — callback for cog tap.
 */

import type { ReactElement } from 'react'
import { AppleChar } from './AppleChar'

export interface TopBarProps {
  /** Show lobby wordmark in center instead of title text. */
  lobby?: boolean
  /** Replace left avatar slot with a back chevron. */
  hasBack?: boolean
  /** Center text (ignored when lobby=true). */
  title?: string
  /** Called when the avatar is tapped (hasBack=false). */
  onAvatar?: () => void
  /** Called when the back glyph is tapped (hasBack=true). */
  onBack?: () => void
  /** Called when the cog icon is tapped. */
  onCog?: () => void
}

export function TopBar({
  lobby = false,
  hasBack = false,
  title,
  onAvatar,
  onBack,
  onCog,
}: TopBarProps): ReactElement {
  return (
    <header className="topbar" role="banner">
      {/* Left slot */}
      <div className="topbar__slot topbar__slot--left">
        {hasBack ? (
          <button
            type="button"
            className="topbar__back"
            aria-label="back"
            onClick={onBack}
          >
            {/* Back chevron — simple SVG, no external dep */}
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M13 4L7 10L13 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : (
          <button
            type="button"
            className="topbar__avatar"
            aria-label="profile"
            onClick={onAvatar}
          >
            <AppleChar size={28} />
          </button>
        )}
      </div>

      {/* Center */}
      <div className="topbar__title" aria-label={lobby ? 'sagwa box' : title}>
        {lobby ? (
          <span className="wordmark">
            <AppleChar size={22} />
            <span>사과상자 · Sagwa Box</span>
          </span>
        ) : (
          <span>{title}</span>
        )}
      </div>

      {/* Right slot */}
      <div className="topbar__slot topbar__slot--right">
        <button
          type="button"
          className="topbar__cog"
          aria-label="settings"
          onClick={onCog}
        >
          {/* Cog — simple SVG */}
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M16.17 7.54l-.9-1.56a1 1 0 00-1.37-.37l-.45.26a4.01 4.01 0 00-1-.58V4.8a1 1 0 00-1-1h-1.8a1 1 0 00-1 1v.49a4 4 0 00-1 .58l-.45-.26a1 1 0 00-1.37.37l-.9 1.56a1 1 0 00.37 1.37l.44.25a4 4 0 000 1.17l-.44.25a1 1 0 00-.37 1.37l.9 1.56a1 1 0 001.37.37l.45-.26a4 4 0 001 .58v.49a1 1 0 001 1h1.8a1 1 0 001-1v-.49a4 4 0 001-.58l.45.26a1 1 0 001.37-.37l.9-1.56a1 1 0 00-.37-1.37l-.44-.25a4 4 0 000-1.17l.44-.25a1 1 0 00.37-1.37z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </header>
  )
}
